/** Per-tenant value table for the detail panel — the flat view (and the ONLY
 *  view while editing). One row per effective entry; edit mode adds removed
 *  (pending-UNSET) rows with a revert affordance and an "(unset)" tail where
 *  a value can be seeded for tenants that don't define the key yet. */
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { MASKED_SECRET, type TenantConfigItem } from '@/api/tourOperatorConfigs';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';

import { batchTenantActions } from '../tenantLinks';

import { getDefaultValue, safePreview } from '../config/schema-grid/schemaOps';

import { useBatchData } from './BatchDataContext';
import { useChangeSet } from './ChangeSetContext';
import type { BatchRow } from './configIndex';
import { DraftValueEditor } from './DraftValueEditor';
import type { EffectiveEntry, RemovedEntry } from './KeyDetailPanel';

const PENDING_CELL_SX = {
  bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, 0.12),
} as const;

/** The tenant name keeps its inline link to that operator's Configuration tab
 *  (same tab, as inline links do); the popover adds the new-tab open, the grid
 *  row and the operator code — which the payload carries here, unlike most
 *  places that only know the id. */
function tenantLink(tenant: TenantConfigItem | undefined, tenantId: number) {
  return (
    <ValueActions actions={batchTenantActions({ id: tenantId, code: tenant?.code })}>
      <Link
        component={RouterLink}
        to={`/tour-operators/${tenantId}/configuration`}
        underline="hover"
        variant="body2"
      >
        {tenant?.name ?? `#${tenantId}`}
      </Link>
    </ValueActions>
  );
}

export function TenantValueTable({
  row,
  entries,
  removedEntries,
  unsetTenants,
  editing,
}: {
  row: BatchRow;
  entries: EffectiveEntry[];
  /** Pending-UNSET entries (edit mode renders them as removed rows). */
  removedEntries: RemovedEntry[];
  unsetTenants: TenantConfigItem[];
  editing: boolean;
}) {
  const { tenantById } = useBatchData();
  const { dispatch } = useChangeSet();
  const [search, setSearch] = useState('');

  const hasWildcard = row.id.includes('[*]');
  const q = search.trim().toLowerCase();

  const nameOf = (tenantId: number) => tenantById.get(tenantId)?.name ?? `#${tenantId}`;

  const matches = (tenantId: number, value: unknown) =>
    !q ||
    nameOf(tenantId).toLowerCase().includes(q) ||
    safePreview(value, 200).toLowerCase().includes(q);

  const sorted = useMemo(
    () =>
      [...entries]
        .filter((e) => matches(e.tenantId, e.value))
        .sort(
          (a, b) =>
            nameOf(a.tenantId).localeCompare(nameOf(b.tenantId)) ||
            a.concretePath.localeCompare(b.concretePath),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, q, tenantById],
  );

  const removedSorted = useMemo(
    () =>
      [...removedEntries]
        .filter((e) => matches(e.tenantId, e.original))
        .sort(
          (a, b) =>
            nameOf(a.tenantId).localeCompare(nameOf(b.tenantId)) ||
            a.concretePath.localeCompare(b.concretePath),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removedEntries, q, tenantById],
  );

  const filteredUnset = unsetTenants.filter((t) => !q || t.name.toLowerCase().includes(q));

  const preview = (value: unknown) => (row.secret ? MASKED_SECRET : safePreview(value, 120));

  const seedValue = (tenant: TenantConfigItem) =>
    dispatch({
      type: 'SET_EDIT',
      tenantId: tenant.id,
      path: row.id,
      before: undefined,
      after: row.schema ? getDefaultValue(row.schema) : '',
      baseTs: tenant.config_last_modified_ts,
    });

  return (
    <Box>
      <TextField
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by tenant or value…"
        sx={{ mb: 1, width: 280 }}
      />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 240 }}>Tenant</TableCell>
            {hasWildcard && <TableCell sx={{ width: 260 }}>Path</TableCell>}
            <TableCell>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((entry) => {
            const tenant = tenantById.get(entry.tenantId);
            return (
              <TableRow key={`${entry.tenantId}:${entry.concretePath}`}>
                <TableCell>{tenantLink(tenant, entry.tenantId)}</TableCell>
                {hasWildcard && (
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {entry.concretePath}
                    </Typography>
                  </TableCell>
                )}
                <TableCell sx={entry.pending ? PENDING_CELL_SX : undefined}>
                  {editing ? (
                    <DraftValueEditor
                      tenantId={entry.tenantId}
                      concretePath={entry.concretePath}
                      schema={row.schema}
                      secret={row.secret}
                      original={entry.original}
                      baseTs={tenant?.config_last_modified_ts ?? null}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}
                      >
                        {/* A secret previews as the mask sentinel, which must
                            never become a copy — hence the `row.secret` gate. */}
                        {row.secret ? (
                          preview(entry.value)
                        ) : (
                          <ValueActions actions={[act.copy(safePreview(entry.value, 4000), 'Copy value')]}>
                            {preview(entry.value)}
                          </ValueActions>
                        )}
                      </Typography>
                      {entry.pending && (
                        <Tooltip title="Revert this change">
                          <IconButton
                            size="small"
                            onClick={() =>
                              dispatch({
                                type: 'REMOVE_EDIT',
                                tenantId: entry.tenantId,
                                path: entry.concretePath,
                              })
                            }
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {/* Pending removals — edit mode only (read views fold them into "(unset)"). */}
          {editing &&
            removedSorted.map((entry) => (
              <TableRow key={`removed:${entry.tenantId}:${entry.concretePath}`}>
                <TableCell>{tenantLink(tenantById.get(entry.tenantId), entry.tenantId)}</TableCell>
                {hasWildcard && (
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {entry.concretePath}
                    </Typography>
                  </TableCell>
                )}
                <TableCell sx={PENDING_CELL_SX}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                        textDecoration: 'line-through',
                        color: 'text.secondary',
                      }}
                    >
                      {preview(entry.original)}
                    </Typography>
                    <Tooltip title="Revert this removal">
                      <IconButton
                        size="small"
                        onClick={() =>
                          dispatch({
                            type: 'REMOVE_EDIT',
                            tenantId: entry.tenantId,
                            path: entry.concretePath,
                          })
                        }
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}

          {/* Tenants without the key — edit mode offers seeding (plain keys only:
              a `[*]` key has no derivable concrete path for a first element). */}
          {editing &&
            filteredUnset.map((tenant) => (
              <TableRow key={`unset:${tenant.id}`}>
                <TableCell>{tenantLink(tenant, tenant.id)}</TableCell>
                {hasWildcard && <TableCell />}
                <TableCell>
                  {hasWildcard ? (
                    <Typography variant="caption" color="text.disabled">
                      (unset)
                    </Typography>
                  ) : (
                    <Button size="small" onClick={() => seedValue(tenant)}>
                      Set value
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {sorted.length === 0 && (!editing || (removedSorted.length === 0 && filteredUnset.length === 0)) && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {q ? 'No tenants match this filter.' : 'No tenant defines this key.'}
        </Typography>
      )}
    </Box>
  );
}
